import { createApp } from 'vue'
import App from './App.vue'
import store from './stores'
import { setupIspInstallPersist } from '@/stores/ispInstallPersist'
import router from './router'
import i18n from './locales'
import Vue3Lottie from 'vue3-lottie'
import * as Sentry from '@sentry/vue'
import { configure } from 'vue-gtag'
import { GAError, GAPerformance } from './utils/ga'
import './types/axios.d.ts'
import ElementPlus from 'element-plus'
import zhTW from 'element-plus/es/locale/lang/zh-tw'
import 'element-plus/dist/index.css'
import './styles/index.scss'
import '@/styles/offset.scss'
// 引入kingnet-icons
import '@/fonts/kingnet-icon/style.css'
import InputTrimDirective from './common/directives/el-intput-trim'
// 引入調試工具
import { autoInitDebugTool } from './utils/debugTool'

// 在 開發環境自動啟用調試工具
autoInitDebugTool()

const app = createApp(App)
app.directive('input-trim', InputTrimDirective)

// 全域錯誤處理
app.config.errorHandler = (err, instance) => {
  console.error('Vue Error:', err)
  const errorMessage = err instanceof Error ? err.message : String(err)
  const componentName = instance?.$options.name ?? 'Unknown'
  GAError.apiError(componentName, 500, errorMessage)
}

// 全域警告處理
app.config.warnHandler = (msg, instance) => {
  const componentName = instance?.$options.name ?? 'Unknown'
  GAError.userWarning(componentName, msg)
}

if (import.meta.env.VITE_APP_SENTRY_DSN) {
  Sentry.init({
    app,
    enabled: import.meta.env.PROD,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    release: `${import.meta.env.VITE_APP_VERSION}.${import.meta.env.VITE_APP_VERSION_DATE}`,
    environment: import.meta.env.MODE,
    maxBreadcrumbs: 50,
    integrations: [
      Sentry.breadcrumbsIntegration({
        console: false // 如果是預設的true，開發時console裡無法定位到錯誤位址
      })
    ]
  })
}

// 初始化 Google Analytics
const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
if (gaMeasurementId) {
  try {
    configure({
      tagId: gaMeasurementId,
      config: {
        params: {
          send_page_view: false,
          debug_mode: import.meta.env.MODE === 'development'
        }
      },
      // 在某些環境（如 LINE WebView）中，外部腳本可能被阻擋
      // 添加錯誤處理避免影響應用運行
      onError: (error: Error) => {
        console.warn('Google Analytics 初始化失敗:', error.message)
      }
    })
  } catch (error) {
    console.warn('Google Analytics 配置錯誤:', error)
  }
}

app.use(store)
// ISP 線上申裝流程：sessionStorage 持久化（重整不掉資料、關分頁即清）
setupIspInstallPersist(store)
app.use(router)
app.use(i18n)
app.use(ElementPlus, {
  locale: zhTW
})
app.use(Vue3Lottie)
app.mount('#app')

// 追蹤頁面載入時間 - 使用現代 Performance API
window.addEventListener('load', () => {
  setTimeout(() => {
    const navEntries = performance.getEntriesByType('navigation')
    if (navEntries.length > 0) {
      const navigation = navEntries[0] as PerformanceNavigationTiming
      const loadTime = navigation.loadEventEnd - navigation.fetchStart
      const pageName = window.location.pathname
      GAPerformance.pageLoadTime(pageName, Math.round(loadTime))
    }
  }, 0)
})
